/**
 * Utkastet til «Avslutt dagen».
 *
 * Oppsummeringen skrives ved skiftslutt av en som er sliten, og den er det
 * eneste i appen som ikke kan gjenskapes — loggpostene ligger i basen, men det
 * regi skriver om dagen finnes bare her. Lukkes panelet ved et uhell, med en
 * sveip eller et feiltrykk, skal ingenting vaere borte.
 *
 * I localStorage og ikke i loggbasen, av samme grunn som paletten og
 * tidskodebryteren: utkastet maa overleve ogsaa naar lagringen ikke er
 * tilgjengelig. Nettopp da — med en base som ikke lot seg aapne — er en halv
 * times skriving det verste man kan miste.
 *
 * Oppsummeringen krypteres likevel. localStorage er klartekst paa disk, og
 * dette er det samme innholdet som ligger kryptert i loggen — det ville vaert
 * meningsloest aa beskytte det ene og ikke det andre. Noekkelen er den samme;
 * se state/krypto.ts.
 *
 * Verdiene lagres som navn og dato, ikke som indekser. Datolista bygges
 * relativt til i dag, saa en indeks peker paa en annen dato etter midnatt — og
 * det er nattskiftet som skriver rapport paa den andre siden av midnatt.
 */

import { erKryptert, krypter } from "./krypto";

/*
 * Noekkelen er PER PRODUKSJON.
 *
 * Her sto én global noekkel, og `lesUtkast` avviste bare paa opptaksdag. To
 * produksjoner paa dag 7 delte dermed utkast og overskrev hverandre — og
 * dette er det eneste i appen som ikke kan gjenskapes.
 *
 * Versjonen er ikke oekt til v3 med vilje. Et utkast som ligger der naa, fra
 * foer produksjonene fikk hver sin noekkel, skal ikke kastes: det flyttes til
 * aktiv produksjon foerste gang det leses. Se `flyttGammelt`.
 */
const NOKKEL_V2 = "infoflyt.avsluttUtkast.v2";

function noekkel(productionId: string): string {
  return `${NOKKEL_V2}.${productionId}`;
}

/**
 * Flytter utkastet fra tiden da det bare fantes én produksjon.
 *
 * Kalles foer hver lesing. Gjoer ingenting naar det ikke er noe aa flytte,
 * og aldri noe naar produksjonen alt har sitt eget.
 */
function flyttGammelt(productionId: string): void {
  try {
    const gammelt = localStorage.getItem(NOKKEL_V2);
    if (gammelt === null) return;
    if (localStorage.getItem(noekkel(productionId)) === null) {
      localStorage.setItem(noekkel(productionId), gammelt);
    }
    localStorage.removeItem(NOKKEL_V2);
  } catch {
    // Utilgjengelig lagring. Da er det ingenting aa flytte heller.
  }
}

export interface AvsluttUtkast {
  readonly shootDay: string;
  readonly summary: string;
  readonly dag: number;
  readonly dateIso: string;
  readonly skift: string;
  readonly enhet: string;
}

/** Utkastet for denne opptaksdagen, eller null. Et utkast fra en annen dag
    gjelder ikke — da er det en ny rapport. */
/**
 * Leses SYNKRONT, med oppsummeringen fortsatt kryptert.
 *
 * Opptaksdag, dato, skift og enhet er oppsett og kan vises med én gang — og
 * det er det som gjoer at panelet ikke blinker tomt. Oppsummeringen dekrypteres
 * etterpaa, fordi WebCrypto er asynkront. `kryptert` sier om den som leser maa
 * vente; er den usann, er teksten alt klar.
 */
export function lesUtkast(
  productionId: string, shootDay: string,
): (AvsluttUtkast & { kryptert: boolean }) | null {
  flyttGammelt(productionId);
  try {
    const raw = localStorage.getItem(noekkel(productionId));
    if (raw === null) return null;
    const u = JSON.parse(raw) as AvsluttUtkast;
    if (typeof u.summary !== "string" || u.shootDay !== shootDay) return null;
    return { ...u, kryptert: erKryptert(u.summary) };
  } catch {
    // Ugyldig eller utilgjengelig lagring. Da er det ikke noe utkast.
    return null;
  }
}

export async function skrivUtkast(productionId: string, u: AvsluttUtkast): Promise<void> {
  try {
    /*
     * Bare oppsummeringen krypteres.
     *
     * Den er den eneste fritteksten her, og den er hele poenget: det regi
     * skriver om dagen. Opptaksdag, dato, skift og enhet er oppsett, ikke
     * innhold — og de maa kunne leses for aa avgjoere om utkastet i det hele
     * tatt gjelder denne dagen.
     */
    const kryptert: AvsluttUtkast = { ...u, summary: await krypter(u.summary) };
    localStorage.setItem(noekkel(productionId), JSON.stringify(kryptert));
  } catch {
    // Privat modus eller full disk. Skrivingen er en bekvemmelighet, ikke en
    // forutsetning — panelet virker som foer.
  }
}

/** Kalles naar rapporten er laget, og naar den lokale loggen slettes paa
    produksjonens end_date. Utkastet er samme slags innhold som loggen og skal
    ikke overleve den. */
export function slettUtkast(productionId: string): void {
  try {
    localStorage.removeItem(noekkel(productionId));
  } catch {
    // Ingenting aa gjoere, og ingenting som avhenger av det.
  }
}
